import React, { useState, useCallback } from 'react';
import {
  DEFAULT_COLOUR_PALETTE,
  getBrandedDefaults,
  COLOUR_GROUPS,
  getKeyLabel,
} from './colourPalette';

const ColourPaletteEditor = ({ formData, setField, branding }) => {
  const overrides = formData.colour_overrides || {};
  const defaults = branding
    ? getBrandedDefaults(branding)
    : DEFAULT_COLOUR_PALETTE;

  const [collapsed, setCollapsed] = useState({});

  const toggleGroup = useCallback((label) => {
    setCollapsed(prev => ({ ...prev, [label]: !prev[label] }));
  }, []);

  const handleColourChange = useCallback((key, value) => {
    setField('colour_overrides', { ...overrides, [key]: value });
  }, [overrides, setField]);

  const handleReset = useCallback((key) => {
    const next = { ...overrides };
    delete next[key];
    setField('colour_overrides', next);
  }, [overrides, setField]);

  const handleResetAll = useCallback(() => {
    setField('colour_overrides', {});
  }, [setField]);

  const hasOverrides = Object.keys(overrides).length > 0;

  return (
    <div className="colour-palette-editor">
      <div className="colour-palette-header">
        <p className="colour-palette-info">
          Edit colours used across all tables and charts. Changes apply globally to this report.
        </p>
        {hasOverrides && (
          <button className="btn-reset-all-colours" onClick={handleResetAll}>
            Reset All to Defaults
          </button>
        )}
      </div>

      {COLOUR_GROUPS.map((group) => {
        const isCollapsed = collapsed[group.label];
        return (
          <div key={group.label} className="colour-group">
            <button
              className={`colour-group-header${isCollapsed ? ' collapsed' : ''}`}
              onClick={() => toggleGroup(group.label)}
            >
              <span className="colour-group-arrow">{isCollapsed ? '▸' : '▾'}</span>
              <span className="colour-group-label">{group.label}</span>
              <span className="colour-group-count">{group.keys.length}</span>
            </button>

            {!isCollapsed && (
              <div className="colour-group-body">
                {group.keys.map((key) => {
                  const currentValue = overrides[key] || defaults[key];
                  const isOverridden = key in overrides;
                  return (
                    <div key={key} className={`colour-row${isOverridden ? ' overridden' : ''}`}>
                      <label className="colour-label">{getKeyLabel(key)}</label>
                      <div className="colour-picker-row">
                        <input
                          type="color"
                          className="colour-swatch"
                          value={currentValue}
                          onChange={(e) => handleColourChange(key, e.target.value)}
                        />
                        <input
                          type="text"
                          className="colour-hex-input"
                          value={currentValue}
                          onChange={(e) => handleColourChange(key, e.target.value)}
                          maxLength={7}
                        />
                        {isOverridden && (
                          <button
                            className="btn-reset-colour"
                            onClick={() => handleReset(key)}
                            title={`Reset to default (${defaults[key]})`}
                          >
                            ↺
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ColourPaletteEditor;
